import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ActionIcon, Autocomplete, Center, Flex, SegmentedControl, Stack, Textarea } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { ArrowUpward, CreateOutlined } from "@mui/icons-material";
import { SvgIcon, Tooltip } from "@mui/material";
import { useMemo } from "react";
import { ContactIntents } from "../../enums/ContactIntent";
import { ContactOutcomes } from "../../enums/ContactOutcomes";
import { ContactTypeOptions } from "../../enums/ContactType";
import { useCreatePriorityNoteMutation, useGetPriorityNotesQuery } from "../../graphql/graphql";
import { doesArrayHaveData } from "../../utils/Array.utils";
import { titleCase } from "../../utils/String.utils";
import { PriorityNote } from "./PriorityNote";
import { TherapyNotesEmptyState } from "../therapy/TherapyNotesEmptyState";
import { AutoHeightContainer } from "../../shared/Layout/AutoHeightContainer";
import { LoadingCards } from "../../shared/Loading/LoadingCards";
import { showErrorNotification } from "../../shared/Notifications";
import { PriorityNoteInitialValues, PriorityNoteSchema } from "./PriorityNotesList.validation";
export const PriorityNotesList = ({ priorityEntryId, caregiverId, patientId, search = "" }) => {
    // Mutations
    const [_, createPriorityNote] = useCreatePriorityNoteMutation();
    // Queries
    const [{ data, fetching }, refetchNotes] = useGetPriorityNotesQuery({
        variables: {
            caregiverId,
            patientId,
            search
        },
        pause: !caregiverId || !patientId
    });
    const priorityNotes = data?.priorityNotes?.priorityNotes ?? [];
    const form = useForm({
        mode: "controlled",
        initialValues: PriorityNoteInitialValues,
        validate: yupResolver(PriorityNoteSchema)
    });
    // Submit a new note
    const submitNote = async (event) => {
        event.preventDefault();
        const valid = form.validate();
        if (valid.hasErrors)
            return;
        const formValues = form.getValues();
        if (!formValues.note)
            return;
        createPriorityNote({
            input: {
                ...formValues,
                priorityEntryId,
                caregiverId,
                patientId
            }
        })
            .then(() => {
            form.setFieldValue("note", "");
            form.setFieldValue("contactType", null);
            form.setFieldValue("contactReason", null);
            form.setFieldValue("contactOutcome", null);
        })
            .catch((error) => {
            console.error("An error occurred.", error);
            showErrorNotification();
        });
    };
    const noteCards = useMemo(() => {
        return fetching ? (Array.from({ length: 9 }, (_, index) => (_jsx(LoadingCards, { numCards: 10, variableHeight: true }, index)))) : doesArrayHaveData(priorityNotes) ? (priorityNotes.map((priorityNote, index) => {
            return _jsx(PriorityNote, { priorityNote: priorityNote }, index);
        })) : (_jsx(TherapyNotesEmptyState, {}));
    }, [fetching, priorityNotes]);
    return (_jsxs(AutoHeightContainer, { children: [_jsx(Stack, { variant: "shadow", p: "xs", style: {
                    flex: 1,
                    overflowY: doesArrayHaveData(priorityNotes)
                        ? "auto"
                        : undefined,
                    minHeight: "0"
                }, children: noteCards }), _jsxs(Stack, { variant: "shadow", p: "xs", bg: "white", children: [_jsxs(Flex, { style: { width: "100%" }, children: [_jsx(SegmentedControl, { transitionTimingFunction: "linear", transitionDuration: 250, size: "sm", color: "var(--mantine-color-primary-9)", style: { marginBottom: "0.5rem", flexShrink: 0 }, data: ContactTypeOptions.map((option) => ({
                                    label: (_jsx(Center, { children: _jsx(Tooltip, { title: titleCase(option.value), placement: "top-start", children: _jsx(SvgIcon, { component: option.label, fontSize: "inherit" }) }) })),
                                    value: option.value
                                })), ...form.getInputProps("contactType") }), _jsxs(Flex, { style: { flex: 1, gap: "20px", marginLeft: "20px" }, children: [_jsx(Autocomplete, { placeholder: "Contact Intent", size: "xs", leftSection: _jsx(SvgIcon, { component: CreateOutlined, fontSize: "inherit" }), style: { flex: 1 }, data: Object.values(ContactIntents), ...form.getInputProps("contactReason") }), _jsx(Autocomplete, { placeholder: "Contact Outcome", size: "xs", leftSection: _jsx(SvgIcon, { component: CreateOutlined, fontSize: "inherit" }), style: { flex: 1 }, data: Object.values(ContactOutcomes), ...form.getInputProps("contactOutcome") })] })] }), _jsx(_Fragment, { children: _jsxs(Flex, { justify: "space-between", p: "xs", style: {
                                borderRadius: "0.65rem",
                                border: "1px solid var(--mantine-color-gray-4)"
                            }, children: [_jsx(Textarea, { variant: "unstyled", placeholder: "Add your new note here... \n" +
                                        "These are only visible to your organization.", autosize: true, minRows: 3, maxRows: 8, style: { width: "100%", marginRight: "1rem" }, ...form.getInputProps("note"), onKeyDown: (event) => {
                                        if (event.key === "Enter") {
                                            submitNote(event);
                                            event.preventDefault();
                                        }
                                    } }), _jsx(ActionIcon, { variant: "filled", mt: "auto", disabled: !form.values.note, onClick: submitNote, children: _jsx(SvgIcon, { component: ArrowUpward, fontSize: "inherit" }) })] }) })] })] }));
};
